import React, { useState } from "react";
import { useLocation, useParams, Link } from "react-router";
import {
  Typography,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Scorecard from "./Scorecard/Scorecard";
import { getComparisonScorecardData } from "./Scorecard/scorecardDataProvider";

interface PatientData {
  patientName: string;
  patientId: string;
  dateOfBirth: string;
  oldPlanName: string;
  newPlanName: string;
}

const PlanComparison = () => {
  const location = useLocation();
  const { recipeName } = useParams<{ recipeName: string }>();
  const patientData = location.state as PatientData;

  // State for confirmation modal
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  // Get the encoded recipe name for URLs
  const encodedRecipeName = encodeURIComponent(recipeName || "");

  const handleApproveClick = () => {
    setShowApprovalModal(true);
  };

  const handleCancelApproval = () => {
    setShowApprovalModal(false);
  };

  const handleConfirmApproval = () => {
    setShowApprovalModal(false);
    // Navigate to blueprint with approval state
    // This will be handled by the Link component's navigation
  };

  return (
    <Stack height={1} gap={2}>
      <Stack spacing={2} overflow="auto">
        {/* Plan Comparison Overview */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            Compare the new plan with the previous plan run for this patient.
          </Typography>

          <Stack direction="row" spacing={3}>
            <Card variant="outlined" sx={{ flex: 1 }}>
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Chip
                    label="Original"
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                  <Typography variant="h6">
                    {patientData?.oldPlanName || "Prostate Nodes R2"} (A)
                  </Typography>
                </Stack>
                <Typography variant="subtitle2" gutterBottom>
                  Key Characteristics:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Standard PTV coverage of D95% {">="} 100%. Equal weighted
                  bladder and rectum sparing.
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              sx={{ flex: 1, borderColor: "primary.main" }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Chip label="Updated" color="primary" size="small" />
                  <Typography variant="h6">
                    {patientData?.newPlanName || "Prostate Nodes R3"} (B)
                  </Typography>
                </Stack>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Key Characteristics:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Standard PTV coverage of D95% {">="} 100%. Preferential
                  weighting on rectum with focus on high dose reduction.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        {/* Scorecard Section */}
        <Scorecard mode="comparison" data={getComparisonScorecardData()} />
      </Stack>

      {/* Buttons */}
      <Stack direction="row" justifyContent="space-between" marginTop="auto">
        <Button
          component={Link}
          to={`/blueprint/${encodedRecipeName}/tracking`}
          variant="outlined"
          color="primary"
          state={patientData}
        >
          Back
        </Button>
        <Stack direction="row" spacing={2}>
          <Button
            component={Link}
            to="/blueprint"
            variant="contained"
            color="error"
          >
            Discard
          </Button>
          <Button
            onClick={handleApproveClick}
            variant="contained"
            color="success"
          >
            Approve
          </Button>
        </Stack>
      </Stack>

      {/* Approval Confirmation Modal */}
      <Dialog
        open={showApprovalModal}
        onClose={handleCancelApproval}
        maxWidth="sm"
      >
        <DialogTitle>Approve Blueprint Modification</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <Typography>
              You are about to approve changes to the blueprint:{" "}
              {recipeName?.replace(/\.toml$/, "")}
            </Typography>
            <Typography>
              Once approved, a new version will be created and the status will
              be set to approved and publish to production. Please ensure all
              details are correct before proceeding.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelApproval}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            component={Link}
            to="/blueprint"
            onClick={handleConfirmApproval}
            variant="contained"
            color="success"
            state={{ planApproved: true }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PlanComparison;
